import React, { useState, useRef, useEffect, useContext } from "react";
import "./SearchBar.css";
import { getAccessToken, getCurrentLocale } from "../../utils/common";
import { handleSessionExpiry } from "../../utils/http";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AccountContext } from "../../context/AccountContext";
import parse from "html-react-parser";
import useMediaQuery from "@mui/material/useMediaQuery";

const SearchBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("Kontakte");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { brandName } = useContext(AccountContext);
  const isVerySmallScreen = useMediaQuery("(max-width:399px)");

  const categories = [
    { key: "Kontakte", label: t("search.categories.contacts") },
    { key: "Übungen", label: t("search.categories.exercises") },
    { key: "Programmvorlagen", label: t("search.categories.programTemplates") }
  ];

  // Search API function
  const performSearch = async (query, category = 'all') => {
    if (!query.trim()) {
      setSearchResults({});
      return;
    }

    setIsLoading(true);
    try {
      const token = getAccessToken();
      const currentLocale = getCurrentLocale();
      
      if (!token) {
        console.error('No access token available');
        setSearchResults({});
        return;
      }

      const url = `${process.env.REACT_APP_SOPHYAPP_API_URL}/${currentLocale}/react/${token}/search/search?q=${encodeURIComponent(query)}&category=${category}&limit=10`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        handleSessionExpiry();
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      } else {
        console.error('Search failed:', response.statusText);
        setSearchResults({});
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults({});
    } finally {
      setIsLoading(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Add dark overlay
      document.body.classList.add('search-overlay-active');
    } else {
      // Remove dark overlay
      document.body.classList.remove('search-overlay-active');
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.classList.remove('search-overlay-active');
    };
  }, [isOpen]);

  const handleSearchClick = () => {
    setIsOpen(true);
  };

  const handleCategoryClick = (category) => {
    setActiveCategory(category);
    if (searchTerm.trim()) {
      performSearch(searchTerm, category);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Debounce search
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      if (value.trim()) {
        performSearch(value, activeCategory);
      } else {
        setSearchResults({});
      }
    }, 300);
    
    setSearchTimeout(timeout);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSearchResults({});
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      setSearchTimeout(null);
    }
  };

  const handleResultClick = (item) => {
    setIsOpen(false);
    setSearchTerm("");
    setSearchResults({});
    
    switch (item.type) {
      case 'kontakt':
        // Force navigation by using window.location for patients to ensure proper re-render
        window.location.href = `/patients/${item.id}`;
        break;
      case 'uebung':
        // For exercises, pass the exercise ID for highlighting
        navigate(`/exercises`, { 
          state: { 
            highlightExerciseId: item.id
          } 
        });
        break;
      case 'programmvorlage':
        navigate(`/program_template/${item.id}`);
        break;
      default:
        break;
    }
  };

  const getItemIcon = (item) => {
    switch (item.type) {
      case 'kontakt':
        return '/images/Benutzer.svg';
      case 'uebung':
        return '/images/uebungen.svg';
      case 'programmvorlage':
        return '/images/programs_icon.svg';
      default:
        return '/images/search.svg';
    }
  };

  const getItemImage = (item) => {
    if (item.type === 'uebung') {
      // Use the same approach as ExerciseCard.js
      if (item.thumbnail) {
        return process.env.REACT_APP_SOPHYAPP_THUMBS_URL + item.thumbnail;
      }
    }
    return null;
  };

      const getExerciseName = (item) => {
        if (item.type !== 'uebung') return item.name || item.title || '';
        
        // Use the same logic as ExerciseCard.js
        const currentLanguage = getCurrentLocale();
        let indexTranslation = 0;
        
        if (item.source_translations && item.source_translations.length > 1) {
          const lang = currentLanguage === "en-US" ? "en" : currentLanguage;
          indexTranslation = item.source_translations.findIndex(
            (element) => element.locale === lang
          );
        }
        
        // Prioritize mapping_sources_translation over source_translations
        return item?.mapping_sources_translation?.[0]?.name
          ? item.mapping_sources_translation[0].name
          : item?.source_translations?.[indexTranslation]?.name
          ? item.source_translations[indexTranslation].name
          : '';
      };

      const getExerciseSteps = (item) => {
        if (item.type !== 'uebung') return '';
        
        // Use the same logic as ExerciseCard.js
        const currentLanguage = getCurrentLocale();
        let indexTranslation = 0;
        
        if (item.source_translations && item.source_translations.length > 1) {
          const lang = currentLanguage === "en-US" ? "en" : currentLanguage;
          indexTranslation = item.source_translations.findIndex(
            (element) => element.locale === lang
          );
        }
        
        // Prioritize mapping_sources_translation over source_translations
        return item?.mapping_sources_translation?.[0]?.steps
          ? item.mapping_sources_translation[0].steps
          : item?.source_translations?.[indexTranslation]?.steps
          ? item.source_translations[indexTranslation].steps
          : '';
      };

      const renderItemSubtitle = (item) => {
        if (item.type === 'kontakt') {
          return item.email || `${item.firstname} ${item.lastname}`.trim();
        } else if (item.type === 'uebung') {
          const steps = getExerciseSteps(item);
          return steps ? steps.replace(/<br\s*\/?>/gi, ' • ') : item.goal || item.body_region;
        } else if (item.type === 'programmvorlage') {
          return item.public ? t("search.template.public") : t("search.template.private");
        }
        return '';
      };

  const renderSearchResults = () => {
    // Map frontend category names to backend keys
    const categoryMap = {
      'Kontakte': 'kontakte',
      'Übungen': 'uebungen', 
      'Programmvorlagen': 'programmvorlagen'
    };
    
    const backendKey = categoryMap[activeCategory] || activeCategory.toLowerCase();
    const results = searchResults[backendKey] || [];
    
    if (results.length === 0) {
      return (
        <div className="no-results">
          <h3>{t("search.noResults.title")}</h3>
          <p>{t("search.noResults.description")}</p>
        </div>
      );
    }

    return (
      <div className="results-list">
        {results.map((item, index) => {
          const itemImage = getItemImage(item);
          const itemIcon = getItemIcon(item);
          const subtitle = renderItemSubtitle(item);
          
          return (
            <div 
              key={`${item.type}-${item.id}-${index}`} 
              className="result-item"
              onClick={() => handleResultClick(item)}
            >
              <div className="result-item-content">
                <div className="result-item-image">
                  {itemImage ? (
                    <img 
                      src={itemImage} 
                      alt={item.name || item.title}
                      className="result-thumbnail"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <img 
                    src={itemIcon} 
                    alt="icon"
                    className={`result-icon ${itemImage ? 'fallback-icon' : ''}`}
                    style={{ display: itemImage ? 'none' : 'block' }}
                  />
                </div>
                    <div className="result-item-text">
                      <div className="result-title">
                        {parse(getExerciseName(item))}
                      </div>
                  {subtitle && (
                    <div className="result-subtitle">
                      {subtitle}
                    </div>
                  )}
                  {item.type === 'uebung' && item.difficulty && (
                    <div className="result-meta">
                      {t("search.exercise.difficulty")}: {item.difficulty}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`search-container ${isOpen ? 'search-active' : ''}`} ref={searchRef}>
      <div className={`search-bar ${isOpen ? 'search-bar-active' : ''}`} onClick={handleSearchClick}>
            <input
              type="text"
              placeholder={
                isVerySmallScreen
                  ? t("search.placeholder_mobile", {
                      defaultValue: t("search.placeholder", { appName: "" })
                    })
                  : t("search.placeholder", { appName: brandName })
              }
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
        {searchTerm ? (
          <span 
            className="search-clear-icon"
            onClick={handleClearSearch}
            title={t("search.clear")}
          >
            ×
          </span>
        ) : (
          <img 
            src="/images/search.svg" 
            alt="search" 
            className="search-icon"
          />
        )}
      </div>
      
      {isOpen && (
        <div className="search-dropdown" ref={dropdownRef}>
          <div className="search-categories">
            {categories.map((category) => (
              <button
                key={category.key}
                className={`category-tab ${
                  activeCategory === category.key ? "active" : ""
                }`}
                onClick={() => handleCategoryClick(category.key)}
              >
                {category.label}
              </button>
            ))}
          </div>
          <div className="search-results">
                {isLoading ? (
                  <div className="loading">
                    <p>{t("search.loading")}</p>
                  </div>
                ) : searchTerm ? (
                  renderSearchResults()
                ) : (
                  <div className="no-results">
                    <h3>{t("search.noResults.title")}</h3>
                    <p>{t("search.noResults.description")}</p>
                  </div>
                )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
